// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/connectors/ConnectorGovernanceTable.jsx

import React from "react";

function statusLabel(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusClass(value) {
  const normalized = String(value || "unknown").toLowerCase().replace(/_/g, "-");
  return `enterprise-admin-status enterprise-admin-status-${normalized}`;
}

function ownerLabel(connector) {
  return connector?.owner?.name || connector?.owner_name || "Unassigned";
}

function accountLabel(connector) {
  return (
    connector?.verified_account?.display ||
    connector?.verified_account?.name ||
    connector?.verified_account?.reference ||
    "Account not yet verified"
  );
}

function dateLabel(value) {
  if (!value) return "No collection yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Collection recorded";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ConnectorGovernanceTable({
  connectors = [],
  selectedConnectorId,
  onSelect,
}) {
  return (
    <section className="enterprise-admin-form-card enterprise-admin-connector-table-card">
      <div className="enterprise-admin-card-heading enterprise-admin-connector-card-heading">
        <p className="enterprise-admin-eyebrow">Connector registry</p>
        <h3>Connected systems</h3>
        <p>Review connection state, verified account, evidence freshness, and accountable ownership.</p>
      </div>

      {!connectors.length ? (
        <div className="enterprise-admin-empty compact">
          <strong>No connectors available</strong>
          <p>Supported providers appear here when enabled in the connector catalog.</p>
        </div>
      ) : (
        <div className="enterprise-admin-table enterprise-admin-connector-table">
          <div className="enterprise-admin-table-row enterprise-admin-table-head" aria-hidden="true">
            <span>Provider</span>
            <span>Status</span>
            <span>Last collection</span>
            <span>Owner</span>
          </div>

          {connectors.map((connector) => {
            const owner = ownerLabel(connector);
            const lifecycle = connector.lifecycle_state || "not_configured";
            const connectorId = connector.connector_id || connector.provider_key;
            const selected = String(selectedConnectorId) === String(connector.connector_id);

            return (
              <button
                key={connector.provider_key || connector.connector_id}
                type="button"
                className={`enterprise-admin-table-row${selected ? " active" : ""}`}
                onClick={() => onSelect?.(connector)}
                aria-pressed={selected}
                aria-label={`Review ${connector.provider_name || connector.provider_key || "connector"}`}
                data-connector-id={connectorId}
              >
                <span className="enterprise-admin-table-primary">
                  <strong>{connector.provider_name || connector.provider_key || "Connector"}</strong>
                  <small>{accountLabel(connector)}</small>
                </span>
                <span className="enterprise-admin-connector-status-cell">
                  <span className={statusClass(lifecycle)}>
                    {connector.status_label || statusLabel(lifecycle)}
                  </span>
                </span>
                <span className="enterprise-admin-table-muted enterprise-admin-connector-collection-cell">
                  {dateLabel(connector.last_successful_collection_at)}
                </span>
                <span className={`enterprise-admin-table-muted${owner === "Unassigned" ? " is-unassigned" : ""}`}>
                  {owner}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
